import { useState } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import useToggledState from '@zenbusiness/application-commons-hooks/useToggledState';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Divider from '@zenbusiness/zui/Divider';
import Text from '@zenbusiness/zui/Text';

import useBankingApplicationStatus from '#client/hooks/useBankingApplicationStatus';
import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';

import { SERVICE_EVENT_PROPERTIES } from './constants';
import * as Styled from './OrderTracker.styles';
import OrderTrackerItem from './OrderTrackerItem';
import OrderTrackerUpsells from './OrderTrackerUpsells';
import { AiSlideIn } from '../AiSlideIn';
import {
  getTimelineItems, getTitleMessageSuffix, shouldFetchBankingStatus, shouldHideBankingTask
} from './utils';
import { HomeSection } from '../HomeSection';

function OrderTracker() {
  const { formatMessage } = useIntl();
  const { orderTasksWithStatus } = useOrderTrackerStatus();
  const [selectedTask, setSelectedTask] = useState(null);
  const {
    state: orderTrackerDetailOpen,
    on: orderTrackerDetailOpenHandler,
    off: orderTrackerDetailCloseHandler
  } = useToggledState(false);

  const allowedTimelineItems = getTimelineItems(orderTasksWithStatus);

  // Check if we have any pending banking tasks that need application status
  const hasPendingBankingTask = allowedTimelineItems.some(shouldFetchBankingStatus);
  const { applicationStatus: bankingApplicationStatus } = useBankingApplicationStatus(
    hasPendingBankingTask
  );

  // Filter out banking tasks with final statuses
  const visibleTimelineItems = allowedTimelineItems.filter(
    (item) => !shouldHideBankingTask(item, bankingApplicationStatus)
  );

  const eventObject = {
    elementName: 'open_order_tracker_smart_assistant',
    pageSlug: 'order_tracker_smart_assistant'
  };

  const closeButtonOrderTrackerPressEvent = useButtonPressedCallback({
    elementName: 'close_order_tracker_smart_assistant',
    elementText: selectedTask?.type,
    elementValue: selectedTask?.type,
    pageSlug: 'order_tracker_smart_assistant'
  });

  const handleOrderTrackerDetailOpen = (item) => {
    setSelectedTask(item);
    orderTrackerDetailOpenHandler();
  };

  const handleOrderTrackerDetailClose = () => {
    setSelectedTask(null);
    orderTrackerDetailCloseHandler();
    closeButtonOrderTrackerPressEvent();
  };
  if (!visibleTimelineItems.length) {
    return null;
  }

  const lastItemIndex = visibleTimelineItems.length - 1;

  const allowedTimelineItemsObject = visibleTimelineItems.reduce(
    (acc, { status, type, data }) => {
      acc[type] = { status };
      if (data?.estimatedFormationDate) {
        acc[type].estimatedFormationDate = data.estimatedFormationDate;
      }
      return acc;
    },
    {}
  );

  return (
    <HomeSection
      title={(
        <Text color="primary" size="xl" weight="medium">
          <FormattedMessage id="dashboard.orderTracker.title" />
        </Text>
      )}
    >
      <Styled.TrackerCard>
        {visibleTimelineItems.map((item, index) => (
          <Styled.ListItemContainer
            key={item.type}
          >
            <OrderTrackerItem
              color={item.color}
              ctaMessageId={`dashboard.orderTracker.orderTaskStatus.${item.status}`}
              eventCallback={useButtonPressedCallback}
              eventObject={eventObject}
              onClick={() => handleOrderTrackerDetailOpen(item)}
              titleMessageId={`dashboard.orderTracker.orderTaskType.${item.type}`}
              titleMessageSuffix={getTitleMessageSuffix(item)}
              type={item.type}
            />
            {
                /* if it's the first card show upsells */
                index === 0 && (<OrderTrackerUpsells type={item.type} />)
              }
            {index !== lastItemIndex && (
            <Divider />
            )}
          </Styled.ListItemContainer>
        ))}
      </Styled.TrackerCard>

      {selectedTask && (
        <AiSlideIn
          title={formatMessage({
            id: `dashboard.orderTracker.orderTaskType.${selectedTask.type}`
          })}
          reasonsReplacements={{
            'order status overview': 'Overview',
            'order status what to expect': 'What to Expect'
          }}
          promptSlug="order-task-smart-assist"
          fillData={{
            product: selectedTask.type,
            data: JSON.stringify(allowedTimelineItemsObject)
          }}
          aiSlideInOpen={orderTrackerDetailOpen}
          closeHandler={handleOrderTrackerDetailClose}
          eventingProperties={{
            elementName: 'order_tracker_smart_assistant',
            elementValue: SERVICE_EVENT_PROPERTIES[selectedTask.type].elementValue,
            elementText: SERVICE_EVENT_PROPERTIES[selectedTask.type].elementText,
            aiProjectName: 'Order Tracker Detail'
          }}
          errorMessage={formatMessage({ id: 'dashboard.orderTracker.errorGeneric' })}
          footer={formatMessage({ id: 'dashboard.orderTracker.footer' })}
        />
      )}
    </HomeSection>
  );
}

export default OrderTracker;
